import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";

interface CommunicationFormProps {
  communityId: string;
  communityName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function CommunicationForm({
  communityId,
  communityName,
  onSuccess,
  onCancel,
}: CommunicationFormProps) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const createCommunication = trpc.communication.create.useMutation({
    onSuccess: () => {
      utils.communication.getNotifications.invalidate();
      utils.communication.getUnreadCount.invalidate();
      toast.success("Comunicado enviado a todos los miembros del barrio");
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || "No se pudo enviar el comunicado");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!communityId || !title.trim() || !body.trim()) return;

    createCommunication.mutate({
      communityId,
      title: title.trim(),
      body: body.trim(),
      attachmentUrls: [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Enviá un mensaje a todos los miembros de {communityName}. Va a aparecer
        en sus notificaciones y también se enviará por email.
      </p>

      <div className="space-y-2">
        <Label htmlFor="title">Título</Label>
        <Input
          id="title"
          placeholder="Comunicado importante"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={createCommunication.isPending}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="body">Mensaje</Label>
        <Textarea
          id="body"
          placeholder="Escribí tu mensaje acá..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          disabled={createCommunication.isPending}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={createCommunication.isPending}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            createCommunication.isPending || !title.trim() || !body.trim()
          }
        >
          {createCommunication.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          Enviar a todos los miembros
        </Button>
      </div>
    </form>
  );
}
